import { h, Component, Host, State, Element, Prop, Watch, Method } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { TableRendererSchemeDefinition, TableRendererSchemeName, TableSize } from "./constants/table-renderer-types";
import { TABLE_RENDERER_SCHEME_CONFIG, TABLE_RENDERER_SCHEME_MAP } from "./constants/table-renderer-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { TableRendererSchemeConfiguration } from "../../constants/colors";
import { isSafari } from "../../utils/device-helper";
import { BackgroundColor } from "../background/constants/background-types";
import sanitizeHtml from "sanitize-html";
import { allowClassesAndLinks } from "../../utils/sanitize-helper";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

@Component({
  tag: "eon-ui-table-renderer",
  styleUrl: "table-renderer.scss",
  shadow: true
})
export class TableRenderer
  implements ColorableComponent<TableRendererSchemeConfiguration<TableRendererSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: TableRendererSchemeConfiguration<TableRendererSchemeDefinition> = TABLE_RENDERER_SCHEME_CONFIG;
  schemeMap = TABLE_RENDERER_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: TableRendererSchemeDefinition;

  @State() noTHead: boolean = false;
  @State() noTFoot: boolean = false;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiTableRendererElement;

  rendererContent: HTMLElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: TableRendererSchemeName;

  /**
   * Highlights the last row of the table.
   */
  @Prop() size: TableSize = "normal";

  /**
   * A tfoot is always highlighted.
   * If no tfoot is generated by the table editor you can still highlight the last row of the table with this feature.
   * If there is a tfoot, this property will be ignored.
   */
  @Prop() highlightLastRow: boolean = false;

  /**
   * Highlights the last column of the table.
   */
  @Prop() highlightLastColumn: boolean = false;

  /**
   * Content of rte renderer as html string
   */
  @Prop() content: string;

  /**
   * This Property is used to handle table columns to be stacked or not for mobile and tablet viewports
   */
  @Prop() doNotStackMobile: boolean = false;

  /**
   * Highlights the row on hover.
   */
  @Prop() highlightRowOnHover: boolean = false;

  componentWillLoad() {
    this.applyScheme();
  }

  componentDidLoad() {
    this.setContent();
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  /**
   * Set Content of Rich Text Editor
   * @param value The value to set the
   * @param format The format to be used, if none provided the format used from the attribute
   */
  @Method()
  async setTableHtml(value: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        this.setContent(value);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("content")
  setContent(content?: string): void {
    let sanitizedContent = content ? content : this.content;

    this.rendererContent.innerHTML = sanitizeHtml(sanitizedContent, allowClassesAndLinks);

    this.sanitizeTable();
    this.setPolyfillCssPrefix(this.rendererContent);
    this.setHighlightStyles(this.rendererContent);
  }

  /*
   * Sanitize table Set the innerText of the tableHead to the tableCell to support mobile tables
   */
  private sanitizeTable(): void {
    const tables = this.rendererContent.querySelectorAll("table");
    this.noTHead = false;
    this.noTFoot = false;

    tables.forEach((table) => {
      const tableRows = table.querySelectorAll("tr");
      table.setAttribute("cellpadding", "0");
      table.setAttribute("cellspacing", "0");
      table.setAttribute("border", "0");

      if (!table.querySelector("thead")) this.noTHead = true;
      if (!table.querySelector("tfoot")) this.noTFoot = true;

      tableRows.forEach((row) => {
        const tableCells = row.querySelectorAll("td");

        tableCells.forEach((cell) => {
          let index = Array.prototype.indexOf.call(tableCells, cell);
          let corresponding_th = table.querySelector("th:nth-child(" + (index + 1) + ")");

          if (corresponding_th) {
            let tableHeadLabel = "";
            const tableHead = corresponding_th as HTMLElement;

            if (tableHead.textContent) {
              tableHeadLabel = tableHead.textContent;
            }

            cell.setAttribute("data-title", tableHeadLabel);
          }

          cell.innerHTML = `<div class='${pfx("cell-wrapper")}'> ${cell.innerHTML} </div>`;
        });
      });
    });
  }

  setPolyfillCssPrefix(content): any {
    let tags = ["table", "thead", "tbody", "tfood", "tr", "td", "th", "col", "colgroup", "sup", "a", "div"];
    tags.forEach((tag) => {
      content.querySelectorAll(tag).forEach((child) => {
        child.classList.add("sc-eon-ui-table-renderer");
      });
    });

    return content;
  }

  setHighlightStyles(content): any {
    content.querySelectorAll("td, th").forEach((cell) => {
      const highlight = cell.querySelector(`span.${pfx("highlight")}`);
      const highlightLine = cell.querySelector(`span.${pfx("highlight-line")}`);
      if (highlight || highlightLine) {
        const boldText = cell.querySelector("b");
        if (boldText) {
          cell.classList.add(pfx("highlight-border"));
        }
        cell.classList.add(pfx("highlight-cell"));
      }
    });

    return content;
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            ["table"]: true
          })}
        >
          <div
            ref={(el) => (this.rendererContent = el as HTMLElement)}
            class={pfx({
              ["table-content"]: true,
              ["highlight-last-row"]: this.highlightLastRow,
              ["highlight-last-column"]: this.highlightLastColumn,
              ["dont-stack"]: this.doNotStackMobile,
              ["no-thead"]: this.noTHead,
              ["no-tfoot"]: this.noTFoot,
              [`size-${this.size}`]: this.size,
              ["highlight-row"]: this.highlightRowOnHover,
              safari: isSafari()
            })}
          ></div>
          <div
            class={pfx({
              ["table-footer"]: true
            })}
          >
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}
